
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, UserMinus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserStore } from "@/store/userStore";

interface AttendeeRowProps {
  attendee: {
    id: string;
    userId: string;
    userFirstName: string;
    userLastName: string;
    phone: string | null;
    profilePhoto: string | null;
    attended: boolean;
    notes: string | null;
    status: string | null;
    clothingSize: string | null;
  };
  isUpdating: boolean;
  isRemoving: boolean;
  classId: string;
  classDate: string;
  onAttendanceChange?: (attended: boolean) => void;
  onRemove?: () => void;
}

export const AttendeeRow: React.FC<AttendeeRowProps> = ({
  attendee,
  onAttendanceChange,
  onRemove,
  classId,
  classDate,
  isUpdating,
  isRemoving
}) => {
  const userName = `${attendee.userFirstName} ${attendee.userLastName}`;
  const { t, i18n } = useTranslation(["athlete", "common"]);
  const isMobile = useIsMobile();
  const { role } = useUserStore();
  const isAdmin = role === 'admin';  
  return (
    <div className={`flex items-center p-2 rounded-md bg-secondary/20 ${isMobile ? "p-0.5" : ""}`}>
      <div className="flex-1">
        <div className={`font-medium ${isMobile ? "text-[10px]" : ""}`}>
          {isMobile && userName.length > 15 ? `${userName.substring(0, 13)}...` : userName}
        </div>
        <div className={`${isMobile ? "text-[9px]" : "text-xs"} text-muted-foreground`}>
            {isAdmin ? `${attendee.phone || t("noPhoneNumber", { ns: "common" })} - ${attendee.clothingSize || t("noSizeProvided", { ns: "common" })}` : attendee.clothingSize || t("noSizeProvided", { ns: "common" })}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {/* Attendance Status Badge */}
        {attendee.status === "cancelled" ?
        <Badge variant="destructive" className={isMobile ? "text-[9px] px-1 py-0 h-4" : ""}>
          {t('history.cancelled', { ns: 'athlete' })}
        </Badge>
        :
        <Badge 
          variant={attendee.attended ? "default" : "outline"} 
          className={`${attendee.attended ? "bg-green-500 hover:bg-green-600 text-white" : ""} ${isMobile ? "text-[9px] px-1 py-0 h-4" : ""}`}
        >
          {attendee.attended ? t("present", { ns: "common" }) : t("notVerified", { ns: "common" })}
        </Badge>}
        {/* Attendance Marking Buttons */}
        {onAttendanceChange && (
          <div className="flex gap-0.5">
            <Button 
              variant="ghost" 
              size={isMobile ? "xs" : "sm"}
              className={`text-green-600 hover:text-green-800 hover:bg-green-100 ${attendee.attended ? 'bg-green-100' : ''} ${isMobile ? "h-5 w-5 p-0" : ""}`}
              onClick={() => onAttendanceChange(true)}
              disabled={isUpdating || attendee.attended || attendee.status === "cancelled" }
            >
              <CheckCircle className={`${isMobile ? "h-3 w-3" : "h-4 w-4"}`} />
            </Button>
            
            <Button 
              variant="ghost" 
              size={isMobile ? "xs" : "sm"}
              className={`text-red-600 hover:text-red-800 hover:bg-red-100 ${!attendee.attended ? 'bg-red-100' : ''} ${isMobile ? "h-5 w-5 p-0" : ""}`}
              onClick={() => onAttendanceChange(false)}
              disabled={isUpdating || !attendee.attended || attendee.status === "cancelled" }
            >
              <XCircle className={`${isMobile ? "h-3 w-3" : "h-4 w-4"}`} />
            </Button>
          </div>
        )}
        
        {/* Remove Client Button */}
        {onRemove && (
          <Button 
            variant="ghost" 
            size={isMobile ? "xs" : "sm"}
            className={`text-red-500 hover:text-red-700 hover:bg-red-100 ${isMobile ? "h-5 w-5 p-0" : ""}`}
            onClick={onRemove}
            disabled={isRemoving}
          >
            <UserMinus className={`${isMobile ? "h-3 w-3" : "h-4 w-4"}`} />
          </Button>
        )}
      </div>
    </div>
  );
};
